import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Button from "../../../components/ui/Button";
import { deleteUserEducation, deleteUserExperience, useAppDispatch } from "../../../redux";
import { useTranslation } from "react-i18next";

interface ExperienceData {
    id?: string;
    title: string;
    company: string;
    period?: string;
    description: string;
    achievements: string[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: ExperienceData[]) => void;
    initialData?: ExperienceData[] | null;
}

const emptyExperience = (): ExperienceData => ({
    title: "",
    company: "",
    period: "",
    description: "",
    achievements: [],
});

const ExperienceModal: React.FC<Props> = ({ open, onClose, onSave, initialData }) => {
    const { t } = useTranslation();
    const [experienceList, setExperienceList] = useState<ExperienceData[]>([]);
    const [achievementInputs, setAchievementInputs] = useState<string[]>([]);
    const [errors, setErrors] = useState<Record<number, { [key: string]: string }>>({});
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (initialData?.length > 0) {
            setExperienceList(initialData);
            setAchievementInputs(initialData.map(() => ""));
        } else {
            // Add one empty experience object by default
            setExperienceList([emptyExperience()]);
            setAchievementInputs([""]);
        }
        setErrors({});
    }, [open, initialData]);

    const handleFieldChange = (index: number, field: string, value: string) => {
        setExperienceList(prevList => {
            const updatedList = [...prevList];
            const exp = { ...updatedList[index] }; // clone the object to ensure mutability

            exp[field] = value; // safe to add new property like 'period'
            updatedList[index] = exp;
            return updatedList;
        });
    };

    const handleAddAchievement = (index: number) => {
        const input = achievementInputs[index].trim();
        if (!input) return; // ignore empty input

        setExperienceList(prevList => {
            const updated = [...prevList];
            const updatedAchievements = [...updated[index].achievements];

            // Only push non-empty achievements
            if (input !== "") {
                updatedAchievements.push(input);
            }

            updated[index] = {
                ...updated[index],
                achievements: updatedAchievements,
            };

            return updated;
        });

        // Clear input
        setAchievementInputs(prevInputs => {
            const newInputs = [...prevInputs];
            newInputs[index] = "";
            return newInputs;
        });
    };

    const handleRemoveAchievement = (expIdx: number, achIdx: number) => {
        const updated = [...experienceList];
        updated[expIdx].achievements.splice(achIdx, 1);
        setExperienceList(updated);
    };

    const handleAddExperience = () => {
        setExperienceList([...experienceList, emptyExperience()]);
        setAchievementInputs([...achievementInputs, ""]);
    };

    const handleRemoveExperience = async (index: number) => {
        const experience = experienceList[index];
        if (experience?.id) {
            await dispatch(deleteUserExperience({ id: experience.id }));
        }
        const updated = [...experienceList];
        updated.splice(index, 1);
        setExperienceList(updated);

        const updatedInputs = [...achievementInputs];
        updatedInputs.splice(index, 1);
        setAchievementInputs(updatedInputs);
    };
    const validate = (): boolean => {
        const validationErrors: Record<number, { [key: string]: string }> = {};

        experienceList.forEach((exp, index) => {
            const errs: { [key: string]: string } = {};
            if (!exp.title || !exp.title.trim()) errs.title = t("experienceModal.errors.titleRequired");
            if (!exp.company || !exp.company.trim()) errs.company = t("experienceModal.errors.companyRequired");
            if (!exp.period || !exp.period.trim()) errs.period = t("experienceModal.errors.periodRequired");
            if (!exp.description || !exp.description.trim()) errs.description = t("experienceModal.errors.descriptionRequired");
            if (!exp.achievements || exp.achievements.filter(a => a.trim() !== "").length === 0) {
                errs.achievements = t("experienceModal.errors.achievementsRequired");
            }

            if (Object.keys(errs).length > 0) {
                validationErrors[index] = errs;
            }
        });
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!validate()) return;
        onSave(experienceList);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto">
            <div className="bg-white p-6 rounded-lg w-full max-w-2xl shadow-lg relative max-h-[80vh] overflow-y-auto">
                <button className="absolute top-6 right-3" onClick={onClose}>
                    <X className="w-5 h-5" />
                </button>
                <h3 className="text-xl font-semibold mb-4">{t("experienceModal.title")}</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <Button type="button" onClick={handleAddExperience} className="mb-4">
                        {t("experienceModal.addNew")}
                    </Button>
                    {experienceList.map((exp, idx) => (
                        <div key={idx} className="border rounded-lg p-4 space-y-4">
                            <div className="flex justify-between items-start">
                                <h4 className="font-medium">{t("experienceModal.experienceNumber")} {idx + 1}</h4>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveExperience(idx)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t("experienceModal.titleLabel")}</label>
                                    <input
                                        type="text"
                                        value={exp.title}
                                        onChange={e => handleFieldChange(idx, "title", e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    {errors[idx]?.title && (
                                        <p className="text-red-500 text-sm mt-2">{errors[idx].title}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t("experienceModal.company")}</label>
                                    <input
                                        type="text"
                                        value={exp.company}
                                        onChange={e => handleFieldChange(idx, "company", e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    {errors[idx]?.company && (
                                        <p className="text-red-500 text-sm mt-2">{errors[idx].company}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("experienceModal.period")}</label>
                                <input
                                    type="text"
                                    value={exp.period}
                                    onChange={e => handleFieldChange(idx, "period", e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                {errors[idx]?.period && (
                                    <p className="text-red-500 text-sm mt-2">{errors[idx].period}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("experienceModal.description")}</label>
                                <textarea
                                    value={exp.description}
                                    onChange={e => handleFieldChange(idx, "description", e.target.value)}
                                    rows={3}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                {errors[idx]?.description && (
                                    <p className="text-red-500 text-sm mt-2">{errors[idx].description}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("experienceModal.achievements")}</label>
                                <div className="space-y-2">
                                    {exp.achievements.map((achievement, achievementIndex) => (
                                        <div key={achievementIndex} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={achievement}
                                                onChange={e => {
                                                    const newValue = e.target.value;
                                                    setExperienceList(prevList => {
                                                        const updatedList = [...prevList];
                                                        const updatedAchievements = [...updatedList[idx].achievements];
                                                        updatedAchievements[achievementIndex] = newValue;
                                                        updatedList[idx] = {
                                                            ...updatedList[idx],
                                                            achievements: updatedAchievements,
                                                        };
                                                        return updatedList;
                                                    });
                                                }}
                                                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveAchievement(idx, achievementIndex)}
                                                className="text-gray-400 hover:text-gray-600">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder={t("experienceModal.addAchievementPlaceholder")}
                                            value={achievementInputs[idx]}
                                            onChange={e => {
                                                const newInputs = [...achievementInputs];
                                                newInputs[idx] = e.target.value;
                                                setAchievementInputs(newInputs);
                                            }}
                                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <Button type="button" onClick={() => handleAddAchievement(idx)}>
                                            {t("experienceModal.addButton")}
                                        </Button>
                                    </div>
                                    {errors[idx]?.achievements && (
                                        <p className="text-red-500 text-sm mt-2">{errors[idx].achievements}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="flex justify-end gap-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            {t("experienceModal.cancel")}
                        </Button>
                        <Button type="submit">{t("experienceModal.saveChanges")}</Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExperienceModal;
